<!-- Component modal to add / edit / remove a person -->
<template>
	<div class="h-full max-h-[80vh] w-full overflow-auto p-6">
		<h2 class="mb-6 text-center text-2xl font-semibold">
			{{ t('presentation.addEditRemovePerson') }}
		</h2>

		<FormKit
			type="repeater"
			v-model="localPlans"
			:actions="false"
			:add-button="false"
			:up-control="false"
			:down-control="false"
			dynamic
			min="1"
			id="people-repeater"
			content-class="w-full flex flex-col"
			item-class="rounded-sm"
			#default="{ value: itemValue, index }"
		>
			<ParticipantCard
				:appointment-id="sessionStore.activeSession?.appointmentId"
				:autoload-latest-plan="false"
				:cloud-sync-enabled="false"
				:index="index"
				:plan="itemValue as Partial<PlanData>"
			/>
		</FormKit>

		<div class="my-6 flex justify-center">
			<button
				class="text-accent-500 hover:text-accent-600 flex font-bold"
				type="button"
				@click="addPerson"
			>
				<PlusCircleIcon class="mr-1 size-6" /> {{ t('newSession.addPlan') }}
			</button>
		</div>

		<!-- Footer buttons -->
		<div class="mt-8 flex flex-col justify-center gap-6">
			<button
				type="button"
				class="bg-accent-400 text-accent-700 hover:bg-accent-700 m-auto rounded-lg px-24 py-2.5 text-base font-bold hover:text-white"
				@click="save"
			>
				{{ t('general.saveUpdates') }}
			</button>

			<button
				type="button"
				class="text-base font-semibold text-red-500"
				@click="$emit('cancel')"
			>
				{{ t('general.cancel') }}
			</button>
		</div>
	</div>
	<PrecoaModal
		class="sm:w-auto!"
		:show="showAddParticipantModal"
		@close="showAddParticipantModal = false"
	>
		<AddParticipantModalContent
			:contacts="unusedContacts"
			@cancel="showAddParticipantModal = false"
			@contact-selected="addNewCard"
		/>
	</PrecoaModal>
</template>

<script setup lang="ts">
import Plan from '@/classes/Plan'
import PrecoaModal from '@/components/common/PrecoaModal.vue'
import type { Contact } from '@/services/CloudSync/cloudSyncPlanService'
import { getUnusedContacts } from '@/services/contactHelper'
import { useSessionStore } from '@/stores/session'
import type { PlanData } from '@/types'
import AddParticipantModalContent from '@/views/NewSession/AddParticipantModalContent.vue'
import ParticipantCard from '@/views/NewSession/ParticipantCard.vue'
import PlusCircleIcon from '@heroicons/vue/24/solid/PlusCircleIcon'
import { computed, onBeforeMount, ref } from 'vue'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()

const emit = defineEmits(['save', 'cancel'])

const sessionStore = useSessionStore()

// Create a copy of the people array that can be mutated without mutating the original
const localPlans = ref(
	sessionStore.activePlans?.map((people) => ({ ...people }) as Plan)
)

const showAddParticipantModal = ref(false)

const contactList = ref<Contact[]>([])

const unusedContacts = computed(() => {
	return getUnusedContacts(localPlans.value, contactList.value ?? [])
})

onBeforeMount(async () => {
	contactList.value = await sessionStore.getContactsForActiveSession()
})

// Add an empty new person to the localPeople array
function addPerson() {
	if (unusedContacts.value?.length) {
		showAddParticipantModal.value = true
	} else {
		localPlans.value.push(new Plan(sessionStore.getPlanPrototype()))
	}
}

const addNewCard = (contact: Contact | Partial<PlanData> | 'new') => {
	showAddParticipantModal.value = false
	if (!localPlans.value) localPlans.value = []

	if (contact === 'new') {
		localPlans.value.push(new Plan(sessionStore.getPlanPrototype()))
	} else {
		localPlans.value.push(new Plan(contact))
	}
}
// Push the localPeople array to the parent
const save = () => {
	emit('save', localPlans.value)
}
</script>
